import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Save, AlertCircle } from 'lucide-react';
import { behaviors } from '../services/api';

const BehaviorDetails = () => {
  const { patientId, behaviorId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [behaviorRecord, setBehaviorRecord] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedRecord, setEditedRecord] = useState(null);

  useEffect(() => {
    fetchBehaviorRecord();
  }, [behaviorId]);

  const fetchBehaviorRecord = async () => {
    try {
      setLoading(true);
      const response = await behaviors.getRecord(behaviorId);
      const record = response.data.data;
      setBehaviorRecord(record);
      setEditedRecord(record);
    } catch (error) {
      console.error('Error fetching behavior record:', error);
      setError('Falha ao carregar registro de comportamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await behaviors.updateRecord(behaviorId, editedRecord);
      setBehaviorRecord(editedRecord);
      setIsEditing(false);
      // Show success notification
      alert('Registro de comportamento atualizado com sucesso');
    } catch (error) {
      console.error('Error updating behavior record:', error);
      setError('Falha ao atualizar registro de comportamento');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center text-red-600 mb-4">
          <AlertCircle className="mr-2" size={20} />
          {error}
        </div>
        <button
          onClick={() => navigate(`/dashboard/patients/${patientId}`)}
          className="text-brand-600 hover:text-brand-900"
        >
          Voltar para Detalhes do Paciente
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center mb-6">
        <button
          onClick={() => navigate(`/dashboard/patients/${patientId}`)}
          className="mr-4 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Detalhes do Comportamento</h1>
          <p className="text-gray-600">
            {behaviorRecord?.behavior?.name}
          </p>
        </div>
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="px-3 py-2 bg-brand-700 text-white rounded-lg text-sm font-medium flex items-center"
          >
            <Edit size={16} className="mr-1" />
            Editar
          </button>
        ) : (
          <button
            onClick={handleSave}
            className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium flex items-center"
          >
            <Save size={16} className="mr-1" />
            Salvar
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="space-y-6">
          {/* Behavior Type */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informações do Comportamento</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Tipo</label>
                <p className="mt-1 text-sm text-gray-900">{behaviorRecord?.behavior?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Categoria</label>
                <p className="mt-1 text-sm text-gray-900">{behaviorRecord?.behavior?.category}</p>
              </div>
            </div>
          </div>

          {/* Intensity and Duration */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Detalhes da Ocorrência</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Intensidade</label>
                {isEditing ? (
                  <input
                    type="number"
                    min="1"
                    max="5"
                    value={editedRecord.intensity}
                    onChange={(e) => setEditedRecord({...editedRecord, intensity: parseInt(e.target.value)})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{behaviorRecord.intensity}/5</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Duração (minutos)</label>
                {isEditing ? (
                  <input
                    type="number"
                    value={editedRecord.duration}
                    onChange={(e) => setEditedRecord({...editedRecord, duration: parseInt(e.target.value)})}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{behaviorRecord.duration} minutos</p>
                )}
              </div>
            </div>
          </div>

          {/* Antecedent and Consequence */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Análise do Comportamento</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Antecedente</label>
                {isEditing ? (
                  <textarea
                    value={editedRecord.antecedent}
                    onChange={(e) => setEditedRecord({...editedRecord, antecedent: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{behaviorRecord.antecedent}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Consequência</label>
                {isEditing ? (
                  <textarea
                    value={editedRecord.consequence}
                    onChange={(e) => setEditedRecord({...editedRecord, consequence: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                  />
                ) : (
                  <p className="mt-1 text-sm text-gray-900">{behaviorRecord.consequence}</p>
                )}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Notas Adicionais</h3>
            {isEditing ? (
              <textarea
                value={editedRecord.notes}
                onChange={(e) => setEditedRecord({...editedRecord, notes: e.target.value})}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            ) : (
              <p className="mt-1 text-sm text-gray-900">{behaviorRecord.notes}</p>
            )}
          </div>

          {/* Recorded Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Informações do Registro</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Registrado em</label>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(behaviorRecord.recorded_at).toLocaleString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Registrado por</label>
                <p className="mt-1 text-sm text-gray-900">
                  {behaviorRecord.recordedBy}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehaviorDetails; 